/** The receipt for a completed trip, ready to print or save as a PDF. */

import { errorMessage, get } from "@taxi/mobile-web/api";
import { longWhen, money, phone } from "@taxi/mobile-web/format";
import { useResource } from "@taxi/mobile-web/hooks";
import { DownloadIcon } from "@taxi/mobile-web/icons";
import { Button, Loading, Notice, TopBar } from "@taxi/mobile-web/ui";
import type { Receipt } from "../lib/types";

export function ReceiptScreen({ id }: { id: string }) {
  const receipt = useResource(() => get<Receipt>(`/customer/bookings/${id}/receipt`), [id]);
  const r = receipt.data;
  return (
    <div className="receipt-screen">
      <TopBar title="Receipt" back={`/bookings/${id}`} />
      <div className="screen-body">
        {receipt.error ? (
          <Notice tone="error">{errorMessage(receipt.error)}</Notice>
        ) : !r ? (
          <Loading />
        ) : (
          <>
            <article className="receipt">
              <header>
                <h2>{r.firm.name}</h2>
                <p>{r.firm.legalName}</p>
                {r.firm.address && <p>{r.firm.address}</p>}
                <p>{phone(r.firm.phone)}</p>
                {r.firm.operatorLicenceNumber && (
                  <p>Operator licence {r.firm.operatorLicenceNumber}</p>
                )}
                {r.firm.vatNumber && <p>VAT number {r.firm.vatNumber}</p>}
              </header>
              <dl className="receipt-meta">
                <div>
                  <dt>Booking</dt>
                  <dd>{r.number}</dd>
                </div>
                <div>
                  <dt>Date</dt>
                  <dd>{longWhen(new Date(r.date))}</dd>
                </div>
                <div>
                  <dt>From</dt>
                  <dd>{r.pickup.address}</dd>
                </div>
                {r.stops.map((stop, i) => (
                  <div key={`${stop.lat},${stop.lng}`}>
                    <dt>Stop {i + 1}</dt>
                    <dd>{stop.address}</dd>
                  </div>
                ))}
                <div>
                  <dt>To</dt>
                  <dd>{r.dropoff.address}</dd>
                </div>
                {r.driver && (
                  <div>
                    <dt>Driver</dt>
                    <dd>
                      {r.driver.name}
                      {r.driver.badgeNumber ? ` (badge ${r.driver.badgeNumber})` : ""}
                    </dd>
                  </div>
                )}
                {r.vehicle && (
                  <div>
                    <dt>Vehicle</dt>
                    <dd>
                      {r.vehicle.registration}
                      {r.vehicle.plateNumber ? ` (plate ${r.vehicle.plateNumber})` : ""}
                    </dd>
                  </div>
                )}
                {r.accountReference && (
                  <div>
                    <dt>Reference</dt>
                    <dd>{r.accountReference}</dd>
                  </div>
                )}
              </dl>
              <table className="receipt-lines">
                <tbody>
                  {r.lines.map((line, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: fare lines have no ids and can repeat.
                    <tr key={`${line.kind}-${i}`}>
                      <td>{line.label}</td>
                      <td>{money(line.amountPence)}</td>
                    </tr>
                  ))}
                  <tr className="subtotal">
                    <td>Fare</td>
                    <td>{money(r.farePence)}</td>
                  </tr>
                  {r.tipPence > 0 && (
                    <tr>
                      <td>Tip</td>
                      <td>{money(r.tipPence)}</td>
                    </tr>
                  )}
                  {r.refundedPence > 0 && (
                    <tr>
                      <td>Refunded</td>
                      <td>−{money(r.refundedPence)}</td>
                    </tr>
                  )}
                  <tr className="total">
                    <td>Total</td>
                    <td>{money(r.totalPence - r.refundedPence)}</td>
                  </tr>
                  {r.vat && (
                    <tr className="vat">
                      <td>Includes VAT at {r.vat.rateBps / 100}%</td>
                      <td>{money(r.vat.amountPence)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              <p className="receipt-paid">
                Paid{" "}
                {r.paymentMethod === "card"
                  ? r.card
                    ? `by ${r.card.brand.charAt(0).toUpperCase() + r.card.brand.slice(1)} •••• ${r.card.last4}`
                    : "by card"
                  : r.paymentMethod === "cash"
                    ? "in cash to the driver"
                    : "on account"}
              </p>
            </article>
            <Button variant="secondary" onClick={() => window.print()}>
              <DownloadIcon size={18} /> Print or save as PDF
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
